import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase/cliente'
import type { PresupuestoObra, CapituloPresupuesto, ItemPresupuesto, VersionPresupuesto } from './tipos'
import { recalcularPresupuesto } from './tipos'
import { PRESUPUESTOS_INICIALES } from './datosInicialesPresupuestos'
import type { Apu, AiuApu } from '../apus/tipos'
import type { ProyectoPlan, PlanEtapa, PlanModulo } from '../tipos'

const STORAGE_KEY = 'mega_presupuestos_v1'
const PROYECTOS_STORAGE_KEY = 'mega_plan_proyectos_v3'
const sb: any = supabase

function cargarLocal(): PresupuestoObra[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((p) => recalcularPresupuesto(p))
      }
    }
  } catch (err) {
    console.error('Error leyendo presupuestos de localStorage:', err)
  }
  return PRESUPUESTOS_INICIALES
}

function guardarLocal(presupuestos: PresupuestoObra[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presupuestos))
    window.dispatchEvent(new CustomEvent('mega:presupuestos-actualizado'))
  } catch (err) {
    console.error('Error guardando presupuestos en localStorage:', err)
  }
}

export function generarSiguienteCodigoPresupuesto(existentes: PresupuestoObra[]): string {
  const anio = new Date().getFullYear()
  const prefijo = `PRE-${anio}-`
  let maxNum = 0
  existentes.forEach((p) => {
    if (p.codigo && p.codigo.startsWith(prefijo)) {
      const num = parseInt(p.codigo.replace(prefijo, ''), 10)
      if (!isNaN(num) && num > maxNum) maxNum = num
    }
  })
  return `${prefijo}${String(maxNum + 1).padStart(3, '0')}`
}

export function usePresupuestos() {
  const [presupuestos, setPresupuestos] = useState<PresupuestoObra[]>(() => cargarLocal())
  const [cargando, setCargando] = useState(false)
  const [presupuestoSeleccionadoId, setPresupuestoSeleccionadoId] = useState<string | null>(() => {
    const lista = cargarLocal()
    return lista.length > 0 ? lista[0].id : null
  })

  // Sincronizar desde Supabase
  const sincronizarDesdeServidor = useCallback(async () => {
    try {
      setCargando(true)
      const { data, error } = await sb
        .from('plan_presupuestos')
        .select('*')
        .eq('activo', true)
        .order('created_at', { ascending: false })

      if (!error && data && data.length > 0) {
        const mapeados = data.map((row: any) =>
          recalcularPresupuesto({
            id: row.id,
            codigo: row.codigo,
            nombre: row.nombre,
            cliente: row.cliente || 'CLIENTE GENERAL',
            ubicacion: row.ubicacion || '',
            proyecto_id: row.proyecto_id || null,
            estado: row.estado || 'borrador',
            aiu: {
              porcentaje_admin: Number(row.porcentaje_admin || 10),
              porcentaje_imprevistos: Number(row.porcentaje_imprevistos || 0),
              porcentaje_utilidad: Number(row.porcentaje_utilidad || 3),
              porcentaje_iva_utilidad: Number(row.porcentaje_iva_utilidad || 19),
            },
            capitulos: Array.isArray(row.capitulos) ? row.capitulos : [],
            versiones: Array.isArray(row.versiones) ? row.versiones : [],
            activo: Boolean(row.activo),
            created_at: row.created_at,
            updated_at: row.updated_at,
          })
        )
        setPresupuestos(mapeados)
        guardarLocal(mapeados)
      }
    } catch (err) {
      console.warn('Usando base local de presupuestos:', err)
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void sincronizarDesdeServidor()

    const onActualizado = () => {
      setPresupuestos(cargarLocal())
    }
    window.addEventListener('mega:presupuestos-actualizado', onActualizado)
    return () => {
      window.removeEventListener('mega:presupuestos-actualizado', onActualizado)
    }
  }, [sincronizarDesdeServidor])

  const presupuestoActivo = presupuestos.find((p) => p.id === presupuestoSeleccionadoId) || presupuestos[0] || null

  // CRUD Presupuesto
  const crearPresupuesto = useCallback(
    (datos: Partial<PresupuestoObra>): PresupuestoObra => {
      const id = datos.id || crypto.randomUUID()
      const codigo = datos.codigo || generarSiguienteCodigoPresupuesto(presupuestos)
      const ahora = new Date().toISOString()

      const nuevo: PresupuestoObra = recalcularPresupuesto({
        id,
        codigo,
        nombre: datos.nombre || 'Nuevo Presupuesto de Obra',
        cliente: datos.cliente || 'MEGA DISTRIBUCIONES SAS',
        ubicacion: datos.ubicacion || 'Bucaramanga, Santander',
        proyecto_id: null,
        estado: 'borrador',
        aiu: datos.aiu || {
          porcentaje_admin: 10,
          porcentaje_imprevistos: 2,
          porcentaje_utilidad: 5,
          porcentaje_iva_utilidad: 19,
        },
        capitulos: [
          {
            id: crypto.randomUUID(),
            orden: 1,
            nombre: 'Capítulo 1: Obras Preliminares e Instalaciones',
            items: [],
            subtotal_capitulo: 0,
          },
        ],
        versiones: [],
        activo: true,
        created_at: ahora,
        updated_at: ahora,
      })

      const actualizados = [nuevo, ...presupuestos]
      setPresupuestos(actualizados)
      guardarLocal(actualizados)
      setPresupuestoSeleccionadoId(nuevo.id)

      // Guardar en Supabase asíncronamente
      void sb
        .from('plan_presupuestos')
        .insert({
          id: nuevo.id,
          codigo: nuevo.codigo,
          nombre: nuevo.nombre,
          cliente: nuevo.cliente,
          ubicacion: nuevo.ubicacion,
          estado: nuevo.estado,
          porcentaje_admin: nuevo.aiu.porcentaje_admin,
          porcentaje_imprevistos: nuevo.aiu.porcentaje_imprevistos,
          porcentaje_utilidad: nuevo.aiu.porcentaje_utilidad,
          porcentaje_iva_utilidad: nuevo.aiu.porcentaje_iva_utilidad,
          costo_directo_total: nuevo.costo_directo_total,
          valor_aiu_total: nuevo.valor_aiu_total,
          total_presupuesto: nuevo.total_presupuesto,
          capitulos: nuevo.capitulos,
          versiones: nuevo.versiones,
          activo: true,
        })
        .then(() => {})

      return nuevo
    },
    [presupuestos]
  )

  const actualizarPresupuesto = useCallback(
    (id: string, updates: Partial<PresupuestoObra>) => {
      setPresupuestos((prev) => {
        const index = prev.findIndex((p) => p.id === id)
        if (index === -1) return prev

        const base = {
          ...prev[index],
          ...updates,
          updated_at: new Date().toISOString(),
        }
        const recalculado = recalcularPresupuesto(base)
        const actualizados = [...prev]
        actualizados[index] = recalculado
        guardarLocal(actualizados)

        // Sincronizar con Supabase
        void sb
          .from('plan_presupuestos')
          .update({
            nombre: recalculado.nombre,
            cliente: recalculado.cliente,
            ubicacion: recalculado.ubicacion,
            estado: recalculado.estado,
            proyecto_id: recalculado.proyecto_id,
            porcentaje_admin: recalculado.aiu.porcentaje_admin,
            porcentaje_imprevistos: recalculado.aiu.porcentaje_imprevistos,
            porcentaje_utilidad: recalculado.aiu.porcentaje_utilidad,
            porcentaje_iva_utilidad: recalculado.aiu.porcentaje_iva_utilidad,
            costo_directo_total: recalculado.costo_directo_total,
            valor_aiu_total: recalculado.valor_aiu_total,
            total_presupuesto: recalculado.total_presupuesto,
            capitulos: recalculado.capitulos,
            versiones: recalculado.versiones,
            updated_at: recalculado.updated_at,
          })
          .eq('id', id)
          .then(() => {})

        return actualizados
      })
    },
    []
  )

  const eliminarPresupuesto = useCallback((id: string) => {
    setPresupuestos((prev) => {
      const filtrados = prev.filter((p) => p.id !== id)
      guardarLocal(filtrados)
      void sb.from('plan_presupuestos').delete().eq('id', id).then(() => {})
      return filtrados
    })
  }, [])

  // Gestión de Capítulos
  const agregarCapitulo = useCallback(
    (presupuestoId: string, nombre?: string) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return
      const orden = pres.capitulos.length + 1
      const nuevoCap: CapituloPresupuesto = {
        id: crypto.randomUUID(),
        orden,
        nombre: nombre || `Capítulo ${orden}: Nueva Etapa de Obra`,
        items: [],
        subtotal_capitulo: 0,
      }
      actualizarPresupuesto(presupuestoId, {
        capitulos: [...pres.capitulos, nuevoCap],
      })
    },
    [presupuestos, actualizarPresupuesto]
  )

  const eliminarCapitulo = useCallback(
    (presupuestoId: string, capituloId: string) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return
      actualizarPresupuesto(presupuestoId, {
        capitulos: pres.capitulos.filter((c) => c.id !== capituloId),
      })
    },
    [presupuestos, actualizarPresupuesto]
  )

  const renombrarCapitulo = useCallback(
    (presupuestoId: string, capituloId: string, nuevoNombre: string) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return
      actualizarPresupuesto(presupuestoId, {
        capitulos: pres.capitulos.map((c) => (c.id === capituloId ? { ...c, nombre: nuevoNombre } : c)),
      })
    },
    [presupuestos, actualizarPresupuesto]
  )

  // Gestión de Ítems / Actividades desde APU
  const agregarItemDesdeApu = useCallback(
    (presupuestoId: string, capituloId: string, apu: Apu, cantidad: number = 1) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return

      const nuevoItem: ItemPresupuesto = {
        id: crypto.randomUUID(),
        apu_id: apu.id,
        codigo: apu.codigo,
        nombre: apu.nombre,
        unidad: apu.unidad || 'UND',
        cantidad: Math.max(0.01, cantidad),
        costo_directo_unitario: Number(apu.costo_directo || 0),
        precio_unitario: Number(apu.precio_unitario_total || apu.costo_directo || 0),
        subtotal: Number(((apu.precio_unitario_total || apu.costo_directo || 0) * cantidad).toFixed(2)),
      }

      const capitulosActualizados = pres.capitulos.map((cap) => {
        if (cap.id !== capituloId) return cap
        return {
          ...cap,
          items: [...cap.items, nuevoItem],
        }
      })

      actualizarPresupuesto(presupuestoId, { capitulos: capitulosActualizados })
    },
    [presupuestos, actualizarPresupuesto]
  )

  const actualizarCantidadItem = useCallback(
    (presupuestoId: string, capituloId: string, itemId: string, cantidad: number) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return

      const capitulosActualizados = pres.capitulos.map((cap) => {
        if (cap.id !== capituloId) return cap
        return {
          ...cap,
          items: cap.items.map((it) => (it.id === itemId ? { ...it, cantidad: Math.max(0, cantidad) } : it)),
        }
      })

      actualizarPresupuesto(presupuestoId, { capitulos: capitulosActualizados })
    },
    [presupuestos, actualizarPresupuesto]
  )

  const eliminarItemCapitulo = useCallback(
    (presupuestoId: string, capituloId: string, itemId: string) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return

      const capitulosActualizados = pres.capitulos.map((cap) => {
        if (cap.id !== capituloId) return cap
        return {
          ...cap,
          items: cap.items.filter((it) => it.id !== itemId),
        }
      })

      actualizarPresupuesto(presupuestoId, { capitulos: capitulosActualizados })
    },
    [presupuestos, actualizarPresupuesto]
  )

  const actualizarAiuPresupuesto = useCallback(
    (presupuestoId: string, aiu: AiuApu) => {
      actualizarPresupuesto(presupuestoId, { aiu })
    },
    [actualizarPresupuesto]
  )

  // Guardar y comparar Versiones (Tarea 3.6)
  const guardarVersionPresupuesto = useCallback(
    (presupuestoId: string, motivo: string): VersionPresupuesto | null => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return null

      const siguienteNum = (pres.versiones?.length || 0) + 1
      const nuevaVersion: VersionPresupuesto = {
        version: siguienteNum,
        fecha: new Date().toISOString(),
        motivo: motivo.trim() || `Versión ${siguienteNum}`,
        total: pres.total_presupuesto,
        capitulos: JSON.parse(JSON.stringify(pres.capitulos)),
        aiu: JSON.parse(JSON.stringify(pres.aiu)),
      }

      actualizarPresupuesto(presupuestoId, {
        versiones: [...(pres.versiones || []), nuevaVersion],
      })

      return nuevaVersion
    },
    [presupuestos, actualizarPresupuesto]
  )

  const restaurarVersionPresupuesto = useCallback(
    (presupuestoId: string, versionNum: number) => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return
      const match = pres.versiones?.find((v) => v.version === versionNum)
      if (!match) return

      actualizarPresupuesto(presupuestoId, {
        capitulos: JSON.parse(JSON.stringify(match.capitulos)),
        aiu: JSON.parse(JSON.stringify(match.aiu)),
      })
    },
    [presupuestos, actualizarPresupuesto]
  )

  // Del Presupuesto Aprobado a la Obra Activa (Tarea 3.8)
  const ganarPresupuestoYConvertirEnObra = useCallback(
    async (presupuestoId: string): Promise<string | null> => {
      const pres = presupuestos.find((p) => p.id === presupuestoId)
      if (!pres) return null

      // Cargar proyectos existentes de localStorage
      let proyectosExistentes: ProyectoPlan[] = []
      try {
        const raw = localStorage.getItem(PROYECTOS_STORAGE_KEY)
        if (raw) proyectosExistentes = JSON.parse(raw)
      } catch {}

      const nuevoProyectoId = crypto.randomUUID()
      const codigoObra = `OBR-${pres.codigo.replace('PRE-', '')}`
      const hoyStr = new Date().toISOString().split('T')[0]
      const fechaFin90Dias = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

      // Convertir Capítulos en Etapas y Actividades en Módulos de Obra con cantidad contratada
      const etapasObra: PlanEtapa[] = pres.capitulos.map((cap, capIdx) => {
        const etapaId = crypto.randomUUID()
        const modulosObra: PlanModulo[] = cap.items.map((it, itemIdx) => {
          return {
            id: crypto.randomUUID(),
            etapa_id: etapaId,
            codigo: it.codigo,
            nombre: it.nombre,
            descripcion: `Actividad contratada según presupuesto ${pres.codigo}`,
            unidad: it.unidad,
            cantidad: it.cantidad,
            cantidad_contratada: it.cantidad,
            cantidad_ejecutada: 0,
            precio_unitario: it.precio_unitario,
            costo_directo_unitario: it.costo_directo_unitario,
            valor_total: it.subtotal,
            horas: Math.round(it.cantidad * 2),
            horas_reales: 0,
            avance: 0,
            estado: 'pendiente',
            orden: itemIdx + 1,
            apu_id: it.apu_id,
            alistamiento_estado: 'sin_alistar',
            colaboradores_ids: [],
            colaboradores: [],
          }
        })

        return {
          id: etapaId,
          proyecto_id: nuevoProyectoId,
          orden: capIdx + 1,
          nombre: cap.nombre,
          fecha_inicio: hoyStr,
          fecha_fin: fechaFin90Dias,
          semanas: 4,
          paralela: false,
          estado: 'por_empezar',
          modulos: modulosObra,
        }
      })

      const nuevoProyecto: ProyectoPlan = {
        id: nuevoProyectoId,
        codigo: codigoObra,
        nombre: pres.nombre,
        cliente: pres.cliente,
        ubicacion: pres.ubicacion,
        fecha_inicio: hoyStr,
        fecha_fin: fechaFin90Dias,
        presupuesto: pres.total_presupuesto,
        tope_contrato: pres.total_presupuesto,
        estado: 'en_ejecucion',
        estado_proyecto: 'en_curso',
        visible_en_matriz: true,
        porcentaje_aiu_contrato: {
          admin: pres.aiu.porcentaje_admin,
          imprevistos: pres.aiu.porcentaje_imprevistos,
          utilidad: pres.aiu.porcentaje_utilidad,
          iva_utilidad: pres.aiu.porcentaje_iva_utilidad,
        },
        etapas: etapasObra,
      }

      // Guardar en almacenamiento local de proyectos
      const proyectosActualizados = [nuevoProyecto, ...proyectosExistentes]
      try {
        localStorage.setItem(PROYECTOS_STORAGE_KEY, JSON.stringify(proyectosActualizados))
        window.dispatchEvent(new CustomEvent('mega:proyectos-plan-actualizado'))
      } catch (err) {
        console.error('Error guardando nuevo proyecto en localStorage:', err)
      }

      // Actualizar estado del presupuesto a 'aprobado' y asociar ID de proyecto
      actualizarPresupuesto(presupuestoId, {
        estado: 'aprobado',
        proyecto_id: nuevoProyectoId,
      })

      // Guardar en Supabase asíncronamente
      try {
        await sb.from('proyectos_plan').insert({
          id: nuevoProyecto.id,
          codigo: nuevoProyecto.codigo,
          nombre: nuevoProyecto.nombre,
          cliente: nuevoProyecto.cliente,
          ubicacion: nuevoProyecto.ubicacion,
          fecha_inicio: nuevoProyecto.fecha_inicio,
          fecha_fin: nuevoProyecto.fecha_fin,
          presupuesto: nuevoProyecto.presupuesto,
          tope_contrato: nuevoProyecto.tope_contrato,
          estado: nuevoProyecto.estado,
          estado_proyecto: nuevoProyecto.estado_proyecto,
          visible_en_matriz: true,
        })

        for (const e of etapasObra) {
          await sb.from('plan_etapas').insert({
            id: e.id,
            proyecto_id: nuevoProyecto.id,
            orden: e.orden,
            nombre: e.nombre,
            fecha_inicio: e.fecha_inicio,
            fecha_fin: e.fecha_fin,
            semanas: e.semanas,
            paralela: e.paralela,
            estado: e.estado,
          })

          for (const m of e.modulos) {
            await sb.from('plan_modulos').insert({
              id: m.id,
              etapa_id: e.id,
              codigo: m.codigo,
              nombre: m.nombre,
              descripcion: m.descripcion,
              unidad: m.unidad,
              cantidad: m.cantidad,
              cantidad_contratada: m.cantidad_contratada,
              cantidad_ejecutada: 0,
              precio_unitario: m.precio_unitario,
              costo_directo_unitario: m.costo_directo_unitario,
              valor_total: m.valor_total,
              horas: m.horas,
              avance: 0,
              estado: 'pendiente',
              orden: m.orden,
              apu_id: m.apu_id,
            })
          }
        }
      } catch (err) {
        console.warn('Sincronización Supabase del proyecto convertido:', err)
      }

      return nuevoProyectoId
    },
    [presupuestos, actualizarPresupuesto]
  )

  return {
    presupuestos,
    presupuestoActivo,
    presupuestoSeleccionadoId,
    setPresupuestoSeleccionadoId,
    cargando,
    crearPresupuesto,
    actualizarPresupuesto,
    eliminarPresupuesto,
    agregarCapitulo,
    eliminarCapitulo,
    renombrarCapitulo,
    agregarItemDesdeApu,
    actualizarCantidadItem,
    eliminarItemCapitulo,
    actualizarAiuPresupuesto,
    guardarVersionPresupuesto,
    restaurarVersionPresupuesto,
    ganarPresupuestoYConvertirEnObra,
    refrescar: sincronizarDesdeServidor,
  }
}

